/**
 * @file CreateUserDialog.tsx
 * @brief Sheet form for creating a user via `POST /api/admin/users`.
 *
 * @author DES
 * @date 26-05-2026
 * @updated 29-07-2026 — FormSheet + GridBlock chrome
 */

import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, LoaderCircle, ShieldCheck, UserRound } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  FormFieldGroup,
  FormSheet,
  FormSheetAlert,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { FormSheetTabs, formSheetTabPanel, type FormSheetTab } from "@/components/ui/FormSheetTabs";
import { Input } from "@/components/ui/Input";
import { TitlePicker } from "@/components/ui/TitlePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ApiClientError } from "@/shared/api/client";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { CompanyPicker } from "@/features/companies/components/CompanyPicker";
import { useIsOwner } from "@/shared/auth/meExtras";
import { useCreateUser } from "../api";
import { CreateUserInputSchema, type CreateUserInput } from "../schemas";

interface CreateUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateUserDialog({ open, onOpenChange }: CreateUserDialogProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [tab, setTab] = useState<CreateUserTab>("profile");
  const createUser = useCreateUser();
  const isOwner = useIsOwner();

  const form = useForm<CreateUserInput>({
    resolver: zodResolver(CreateUserInputSchema),
    defaultValues: {
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      phone_number: "",
      title: "",
      role: "user",
      status: "active",
      company_id: "",
    },
  });

  useEffect(() => {
    if (open) {
      form.reset();
      setShowPassword(false);
      setServerError(null);
      setTab("profile");
    }
  }, [open, form]);

  const onSubmit = form.handleSubmit(
    async (values) => {
      setServerError(null);
      try {
        const cleaned: CreateUserInput = {
          ...values,
          company_id: values.company_id?.trim() ? values.company_id : undefined,
          phone_number: values.phone_number?.trim() ? values.phone_number : undefined,
          title: values.title?.trim() ? values.title : undefined,
        };
        await createUser.mutateAsync(cleaned);
        toast({
          variant: "success",
          title: "User created",
          description: `${values.email} has been added.`,
        });
        onOpenChange(false);
      } catch (err) {
        if (err instanceof ApiClientError) {
          setServerError(err.message);
        } else {
          setServerError("Unexpected error. Please try again.");
        }
      }
    },
    // A field in a hidden tab failed: open that tab so the error shows
    // (DES 01-10-2026: form standard, as Edit user).
    (errs) => {
      const keys = Object.keys(errs);
      if (keys.some((k) => ["role", "status", "company_id"].includes(k))) setTab("access");
      if (
        keys.some((k) =>
          ["title", "first_name", "last_name", "email", "phone_number", "password"].includes(k),
        )
      ) {
        setTab("profile");
      }
    },
  );

  const errors = form.formState.errors;

  return (
    <FormSheet open={open} onOpenChange={onOpenChange}>
      {/* DES 01-10-2026: form standard. Opens like Cmd+K, as Edit user. */}
      <FormSheetContent side="palette">
        <form
          onSubmit={onSubmit}
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          noValidate
        >
          <FormSheetFrame>
            <FormSheetHeader>
              <FormSheetTitle>Create user</FormSheetTitle>
              <FormSheetDescription>
                Adds a new account. The user will be able to sign in immediately with the
                credentials below.
              </FormSheetDescription>
            </FormSheetHeader>

            <FormSheetTabs
              tabs={CREATE_USER_TABS}
              value={tab}
              onChange={setTab}
              label="Create user"
            />

            <FormSheetBody>
              {/* DES 01-10-2026: form standard. Same tabs as Edit user where
                  the section exists in Create (Signup and Subscription only
                  exist once the account does). Hidden panels stay mounted. */}
              <div {...formSheetTabPanel(tab, "profile")}>
                <FormSheetSection
                  hideHeading
                  title="Profile"
                  subtitle="Identity, contact details and the password they sign in with."
                >
                  {/* Title, first and last name in one row, like Edit user;
                      then email (two columns) and phone, then password. */}
                  <FormSheetFields className="sm:grid-cols-3">
                    <FormSheetField>
                      <FormFieldGroup label="Title (optional)" error={errors.title?.message}>
                        <TitlePicker
                          value={form.watch("title") || null}
                          onChange={(label) => form.setValue("title", label ?? "")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup
                        label="First name"
                        error={errors.first_name?.message}
                        required
                      >
                        <Input
                          capitalizeFirst
                          autoComplete="given-name"
                          placeholder="Jane"
                          {...form.register("first_name")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Last name" error={errors.last_name?.message} required>
                        <Input
                          capitalizeFirst
                          autoComplete="family-name"
                          placeholder="Doe"
                          {...form.register("last_name")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField span={2}>
                      <FormFieldGroup label="Email" error={errors.email?.message} required>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder="user@example.com"
                          {...form.register("email")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Phone (optional)" error={errors.phone_number?.message}>
                        <Input
                          autoComplete="tel"
                          placeholder="+44 …"
                          {...form.register("phone_number")}
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField span={2}>
                      <FormFieldGroup
                        label="Password"
                        hint="Min 8 chars · upper + lower + number + symbol · no spaces."
                        error={errors.password?.message}
                        required
                      >
                        <div className="relative">
                          <Input
                            type={showPassword ? "text" : "password"}
                            autoComplete="new-password"
                            className="pr-10"
                            {...form.register("password")}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground"
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              <div {...formSheetTabPanel(tab, "access")}>
                <FormSheetSection
                  hideHeading
                  title="Access"
                  subtitle="Role controls permissions; status controls whether they can sign in. Attach a company for corporate billing, or leave it empty for an individual user."
                >
                  {/* Role, Status, Company in one row (DES 01-10-2026: no full-width short fields). */}
                  <FormSheetFields className="sm:grid-cols-3">
                    <FormSheetField>
                      <FormFieldGroup label="Role" error={errors.role?.message} required>
                        <Select
                          value={form.watch("role")}
                          onValueChange={(v) => form.setValue("role", v as CreateUserInput["role"])}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Company Admin</SelectItem>
                            {/* Only the Owner makes Super Admins (B31). */}
                            {isOwner ? <SelectItem value="superadmin">Super Admin</SelectItem> : null}
                          </SelectContent>
                        </Select>
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup label="Status" error={errors.status?.message} required>
                        <Select
                          value={form.watch("status")}
                          onValueChange={(v) =>
                            form.setValue("status", v as CreateUserInput["status"])
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="suspended">Suspended</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormFieldGroup>
                    </FormSheetField>
                    <FormSheetField>
                      <FormFieldGroup
                        label="Company (optional)"
                        error={errors.company_id?.message}
                      >
                        <CompanyPicker
                          value={form.watch("company_id") || null}
                          onChange={(id) => form.setValue("company_id", id ?? "")}
                          placeholder="Select a company…"
                        />
                      </FormFieldGroup>
                    </FormSheetField>
                  </FormSheetFields>
                </FormSheetSection>
              </div>

              {serverError ? <FormSheetAlert>{serverError}</FormSheetAlert> : null}
            </FormSheetBody>

            <FormSheetFooter>
              <FormSheetFooterCell>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={createUser.isPending}
                  className={formSheetActionClass}
                >
                  Cancel
                </Button>
              </FormSheetFooterCell>
              <FormSheetFooterCell>
                <Button
                  type="submit"
                  disabled={createUser.isPending}
                  className={cn(
                    formSheetActionClass,
                    "bg-primary text-primary-foreground hover:bg-primary/90",
                  )}
                >
                  {createUser.isPending ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Creating…
                    </>
                  ) : (
                    "Create user"
                  )}
                </Button>
              </FormSheetFooterCell>
            </FormSheetFooter>
          </FormSheetFrame>
        </form>
      </FormSheetContent>
    </FormSheet>
  );
}

type CreateUserTab = "profile" | "access";

// Same ids, labels and icons as Edit user's tabs (DES 01-10-2026: form standard).
const CREATE_USER_TABS: FormSheetTab<CreateUserTab>[] = [
  { id: "profile", label: "Profile", icon: UserRound },
  { id: "access", label: "Access", icon: ShieldCheck },
];
